'use client';

import geoContract from '@bells/api/geo/frontend';
import { useLanguage } from '@bells/core/i18n';
import { generateRestClient } from '@bells/core/rest/react';
import { locationSearchPlaceholder } from '@bells/i18n';
import { FoundLocation } from '@bells/schema/models/mapbox';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebounce } from 'use-debounce';
import { Command, CommandInput } from './command';

import { cn } from '@bells/core/lib/utils';
import {
  LocationSearchInputProps,
  LocationSearchResult,
} from './location-search-input';
import { Spinner } from './spinner';

const systemRestClient = generateRestClient(geoContract);
export type LocationSearchListRef = {
  reset: () => void;
};

const LocationSearchList = forwardRef<
  LocationSearchListRef,
  LocationSearchInputProps
>(
  (
    {
      disabled,
      onLoading,
      placeholder,
      query,
      className,
      term,
      selectedId: initSelectedId,
      onResolve,
    },
    ref
  ) => {
    const placeholderText = useMemo(() => {
      return placeholder ?? locationSearchPlaceholder();
    }, [placeholder]);

    const { language } = useLanguage();
    const [initTerm] = useState<string | undefined>(term);

    const [searchInput, setSearchInput] = useState<string | undefined>(term);
    const [selectedId, setSelectedId] = useState<string | undefined>(
      initSelectedId
    );
    const [debouncedSearchInput] = useDebounce(searchInput, 150);
    const canSearch = useRef<boolean>(false);

    useImperativeHandle(ref, () => ({
      reset: () => {
        setSelectedId(undefined);
        setSearchInput(undefined);
        loadData();
      },
    }));

    const [data, setData] = useState<FoundLocation[]>([]);
    const [isPendingLocation, setIsPendingLocation] = useState<boolean>(false);
    const [resolvedIsLoading, setResolveIsLoading] = useState<boolean>(false);
    const previousIdRef = useRef<string | undefined>(undefined);

    useEffect(() => {
      if (canSearch.current) {
        loadData(debouncedSearchInput);
      }
    }, [debouncedSearchInput]);

    const loadData = useCallback(
      async (term?: string) => {
        setIsPendingLocation(true);
        setData([]);
        onResolve?.();
        setSelectedId(undefined);
        const result = await systemRestClient.findLocation.mutate({
          body: {
            term: term,
          },
          query: {
            ...query,
            locale: language,
          },
        });

        if (result.status === 200) {
          setData(result.body.items);
        }
        setIsPendingLocation(false);
      },
      [debouncedSearchInput]
    );

    const selectItem = useCallback(
      async (id?: string) => {
        if (!id || id === previousIdRef.current) {
          return;
        }

        previousIdRef.current = id;
        setResolveIsLoading(true);
        const result = await systemRestClient.resolveFindLocation.query({
          params: { id: id },
          query: {
            language,
          },
        });

        if (result.status === 200) {
          onResolve?.(result.body);
        }
        setResolveIsLoading(false);
      },
      [language]
    );

    const loadInit = async () => {
      setIsPendingLocation(true);
      const result = await systemRestClient.findLocation.mutate({
        body: {
          term: debouncedSearchInput,
        },
        query: {
          ...query,
          locale: language,
        },
      });

      if (result.status === 200) {
        setData(result.body.items);
      }
      setIsPendingLocation(false);
      canSearch.current = true;
    };

    useEffect(() => {
      loadInit();
    }, [initTerm]);

    const isLoadingResolve = useMemo(() => {
      return resolvedIsLoading || disabled;
    }, [resolvedIsLoading, disabled]);

    const isLoading = useMemo(() => {
      return isPendingLocation || resolvedIsLoading;
    }, [isPendingLocation, resolvedIsLoading]);

    useEffect(() => {
      onLoading?.(resolvedIsLoading);
    }, [resolvedIsLoading]);

    return (
      <Command
        className={cn('relative bg-transparent', className)}
        value={selectedId}
        shouldFilter={false}
      >
        <CommandInput
          disabled={isLoadingResolve}
          withBorder={true}
          value={searchInput ?? ''}
          className='bg-transparent font-medium'
          onValueChange={(val) => setSearchInput(val)}
          placeholder={placeholderText}
        />
        {isLoading && (
          <Spinner className='absolute right-2 top-2 z-[4] size-6'></Spinner>
        )}

        <LocationSearchResult
          setAddress={(val) => {
            setSelectedId(val.id);
            selectItem(val.id);
          }}
          selected={selectedId}
          term={searchInput}
          data={data ?? []}
          isLoading={isPendingLocation}
        ></LocationSearchResult>
      </Command>
    );
  }
);

export { LocationSearchList };
